import { DecimalPipe, LowerCasePipe, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  AppointmentStatus,
  PropertyApplicationDto,
  PropertyApplicationPendingActionType,
  PropertyApplicationStatus,
  PropertyDto,
  UserDto,
} from '@workspace/data-types';
import {
  AvatarAuraComponent,
  BaseDataTableCoreComponent,
  DataTableComponent,
  LuxonDatePipe,
} from '@workspace/ng-aura';
import { PropertyHoverPreviewDirective } from '../../../property/components/property-hover-card';
import { PropertyApplicationColumnKey } from './abstract-property-applications-data-table.component';

@Component({
  selector: 'etxe-property-applications-data-table',
  templateUrl: './property-applications-data-table.shared.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DataTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatButtonModule,
    MatCellDef,
    MatCell,
    LuxonDatePipe,
    LowerCasePipe,
    UpperCasePipe,
    DecimalPipe,
    TitleCasePipe,
    MatIconModule,
    MatTooltipModule,
    RouterModule,
    AvatarAuraComponent,
    PropertyHoverPreviewDirective,
  ],
})
export class PropertyApplicationsDataTableSharedComponent extends BaseDataTableCoreComponent<
  PropertyApplicationDto,
  PropertyApplicationColumnKey
> {
  hideSearch = input<boolean>(false);
  searchPlaceHolder = input<string>('Rechercher par référence, titre, ville...');

  getProperty(item: PropertyApplicationDto): PropertyDto | null {
    if (item.property && typeof item.property === 'object' && '_id' in item.property) {
      return item.property as PropertyDto;
    }
    return null;
  }

  getOwner(item: PropertyApplicationDto): UserDto | null {
    const prop = this.getProperty(item);
    if (prop?.owner && typeof prop.owner === 'object') {
      return prop.owner as UserDto;
    }
    return null;
  }

  hasAppointmentOccurred(item: PropertyApplicationDto): boolean {
    if (
      item.status === PropertyApplicationStatus.VISITED ||
      item.status === PropertyApplicationStatus.OFFER_SUBMITTED ||
      item.status === PropertyApplicationStatus.ACCEPTED
    ) {
      return true;
    }

    const appt = item.appointment as unknown as Record<string, unknown> | undefined;
    if (appt && typeof appt === 'object') {
      if (appt['status'] === AppointmentStatus.COMPLETED) {
        return true;
      }
      const confirmedSlot = appt['confirmedSlot'] as { startAt?: string } | undefined;
      if (confirmedSlot?.startAt && new Date(confirmedSlot.startAt) <= new Date()) {
        return true;
      }
    }

    const owner = this.getOwner(item);
    return !!owner?.identity?.avatar?.url;
  }

  getMaskedOwnerName(identity?: { firstName?: string; lastName?: string }): string {
    if (!identity) return '-';
    const first = identity.firstName ? identity.firstName.trim() : '';
    let last = identity.lastName ? identity.lastName.trim() : '';
    if (last && !last.endsWith('.')) {
      last = `${last.charAt(0).toUpperCase()}.`;
    }
    return [first, last].filter(Boolean).join(' ') || '-';
  }

  getCoverPhotoUrl(item: PropertyApplicationDto): string | null {
    const prop = this.getProperty(item);
    const cover = prop?.media?.coverPhoto;
    if (cover && typeof cover === 'object' && 'url' in cover) {
      return (cover as { url?: string }).url || null;
    }
    return null;
  }

  getSellingPriceEuros(item: PropertyApplicationDto): number | null {
    const prop = this.getProperty(item);
    const cents = prop?.financials?.sellingPriceCents;
    return cents ? cents / 100 : null;
  }

  getEstimatedCommissionEuros(item: PropertyApplicationDto): number | null {
    const price = this.getSellingPriceEuros(item);
    const rate = item.engagement?.commissionRate;
    if (!price || !rate) return null;
    return Math.round((price * rate) / 100);
  }

  getPendingActionLabel(type?: PropertyApplicationPendingActionType): string {
    switch (type) {
      case PropertyApplicationPendingActionType.VISIT_PROPOSAL:
        return 'Créneaux de visite proposés';
      case PropertyApplicationPendingActionType.VISIT_RESCHEDULE:
        return 'Reprogrammation demandée';
      case PropertyApplicationPendingActionType.FINAL_OFFER:
        return 'Offre définitive à déposer';
      case PropertyApplicationPendingActionType.MANDATE_SELECTION:
        return 'Sélection finale du vendeur';
      default:
        return 'Action en cours';
    }
  }
}
